import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { useBoardChartData } from '../../hooks/useBoardChartData';
import { BaseChartProps } from './types';
import { CHART_COLORS, CHART_STYLES } from './chartTheme';
import { useSetChartMetadata } from './ChartContext';

export const OrderFulfillmentFunnelChart: React.FC<BaseChartProps> = () => {
    const { t } = useTranslation();
    const { events, isLoading } = useBoardChartData();

    const chartData = useMemo(() => {
        if (!events) return [];
        const orderEvents = events.filter((e: any) => e.category === 'order');
        const placed = orderEvents.filter((e: any) => (e.type || e.eventType) === 'placed').length;
        const confirmed = orderEvents.filter((e: any) => (e.type || e.eventType) === 'confirmed').length;
        const fulfilled = orderEvents.filter((e: any) => (e.type || e.eventType) === 'fulfillment_success').length;
        if (placed === 0 && confirmed === 0 && fulfilled === 0) return [];
        return [
            { name: t('charts.placed', 'Placed'), value: placed, color: CHART_COLORS.primary },
            { name: t('charts.confirmed', 'Confirmed'), value: confirmed, color: CHART_COLORS.chart.line3 },
            { name: t('charts.fulfilled', 'Fulfilled'), value: fulfilled, color: CHART_COLORS.revenue },
        ];
    }, [events, t]);

    const isEmpty = !isLoading && chartData.length === 0;
    useSetChartMetadata({ isEmpty });

    const rates = useMemo(() => {
        if (chartData.length < 3) return [];
        return [
            chartData[0].value > 0 ? ((chartData[1].value / chartData[0].value) * 100).toFixed(0) : '0',
            chartData[1].value > 0 ? ((chartData[2].value / chartData[1].value) * 100).toFixed(0) : '0',
        ];
    }, [chartData]);

    return (
        <div className="w-full h-full bg-transparent overflow-hidden flex flex-col">
            <div className="flex-1 min-h-0 p-2">
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData} margin={CHART_STYLES.margin.withAxis}>
                        <CartesianGrid strokeDasharray={CHART_STYLES.grid.strokeDasharray} vertical={false} stroke={CHART_STYLES.grid.stroke} opacity={CHART_STYLES.grid.opacity} />
                        <XAxis dataKey="name" {...CHART_STYLES.axis} tick={{ ...CHART_STYLES.axis.tick, fontSize: 10 }} />
                        <YAxis {...CHART_STYLES.axis} tick={{ ...CHART_STYLES.axis.tick, fontSize: 10 }} width={40} />
                        <Tooltip contentStyle={CHART_STYLES.tooltip.contentStyle} />
                        <Bar dataKey="value" radius={[4, 4, 0, 0]} maxBarSize={60}>
                            {chartData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            </div>
            {rates.length === 2 && (
                <div className="flex justify-center gap-6 pb-2">
                    <span className="text-[10px]" style={{ color: CHART_COLORS.gray[500] }}>→ {rates[0]}% {t('charts.confirmRate', 'confirmed')}</span>
                    <span className="text-[10px]" style={{ color: CHART_COLORS.gray[500] }}>→ {rates[1]}% {t('charts.fulfillRate', 'fulfilled')}</span>
                </div>
            )}
        </div>
    );
};
